/**
 * Loose-FK resolver — picks the right `LooseRelationship` from the set of
 * declarations on a single `(fromSchema, fromTable, fromColumn)` triple,
 * keyed by `sourcePath`. A JSON column can host multiple loose FKs (Pass 3,
 * Slice A) — e.g. `$.customer_id → customers.id` AND
 * `$.warehouse_id → warehouses.id` — so callers passing a JSON path get the
 * matching declaration instead of "the first one we found".
 */
import type { LooseRelationship } from '../../../../types/electron';
import type { FilterGroup } from '../../core/types/workspaceTypes';

/**
 * All loose-FK declarations whose source is `(schema, table, column)`.
 * Returned in declaration order — store insertion order is stable.
 */
export function findLooseRelationshipsForColumn(
  looseRelationships: readonly LooseRelationship[] | undefined,
  schema: string,
  table: string,
  column: string,
): LooseRelationship[] {
  if (!looseRelationships) return [];
  return looseRelationships.filter(
    (r) =>
      r.fromSchema === schema &&
      r.fromTable === table &&
      r.fromColumn === column,
  );
}

/**
 * Pick the active declaration for a given `sourcePath` from a list of
 * candidate loose FKs that all share `(fromSchema, fromTable, fromColumn)`.
 *
 * - When `sourcePath` is a string (incl. empty), return the rel whose
 *   `sourcePath` strictly matches.
 * - When `sourcePath` is `undefined`:
 *     - If exactly one declaration exists, return it (preserves the
 *       Pass-2 single-FK behavior).
 *     - If multiple declarations exist, return `null` — callers should
 *       prompt the user to disambiguate (a chooser UI).
 *     - If none exist, return `null`.
 *
 * No-path declarations (i.e. `sourcePath == null`) are addressable by
 * passing `sourcePath: undefined` to this function only when they're the
 * sole match — passing `null` explicitly is reserved for future use if we
 * ever need to distinguish "no path declared" from "any path accepted".
 */
export function resolveActiveLooseRel(
  looseRels: readonly LooseRelationship[],
  sourcePath: string | undefined,
): LooseRelationship | null {
  if (looseRels.length === 0) return null;
  if (typeof sourcePath === 'string') {
    return looseRels.find((r) => (r.sourcePath ?? '') === sourcePath) ?? null;
  }
  // sourcePath is undefined — fall back to the lone no-path declaration if
  // there is exactly one rel; otherwise the caller must disambiguate.
  if (looseRels.length === 1) return looseRels[0] ?? null;
  return null;
}

/**
 * Whether a REAL FK constraint on a column outranks a loose declaration on the
 * same column — the one precedence rule every FK map applies (Table
 * Explorer's `buildForeignKeyMap`, the VQE column adapter
 * `toTableExplorerColumn`, the Query Editor's `useResultSetRealFkMap`).
 *
 * Only a SINGLE-column constraint does. A column that is merely one member of
 * a composite constraint (`(tenant_id, customer_id) → customers(tenant_id,
 * id)`) has no single-column target: its per-column pairing (`customer_id →
 * customers.id`) ignores the other key columns, so a lookup through it can
 * preview or offer another tenant's row. A loose declaration the user made on
 * that column is an explicit single-column statement (typically with the
 * `targetFilter` that scopes the other key), so it wins there; without one the
 * column keeps its per-column pairing as before.
 */
export function realFkOutranksLoose(constraintColumnCount: number): boolean {
  return constraintColumnCount <= 1;
}

/** What `compareFkConstraints` ranks a real FK constraint by. */
export type FkConstraintRank = {
  /** The constraint's name (SQLite reports none: both of its readers call FK #n `fk_<n>`). */
  readonly name?: string;
  readonly columnCount: number;
};

/**
 * The order in which the real FK constraints on ONE column compete to be its
 * target; the first one covering the column wins. The one rule every FK
 * surface applies — Table Explorer's `buildForeignKeyMap` (its grid cells,
 * cell editor and filter chip), the Query Editor's `useResultSetRealFkMap`
 * (built by `buildForeignKeyMap`: cell editor, enum editor's "Search rows"),
 * the grid FK cell preview (`resolveFkTarget`) and the VQE column adapter (the
 * resolver's `buildFkColumnIndex`) — so a column with two FKs previews,
 * edits and looks up through the same table everywhere:
 *
 *  1. a single-column constraint before a composite one the column is only a
 *     member of (`realFkOutranksLoose`);
 *  2. then by constraint name, code-unit order (never locale-dependent).
 *
 * Why the name: the catalogs list constraints in an engine- and
 * query-dependent order (SQLite's pragma even lists the last-declared FK
 * first), while the name is the same in every reader and on every machine.
 */
export function compareFkConstraints(a: FkConstraintRank, b: FkConstraintRank): number {
  const composite = Number(!realFkOutranksLoose(a.columnCount)) - Number(!realFkOutranksLoose(b.columnCount));
  if (composite !== 0) return composite;
  const x = a.name ?? '';
  const y = b.name ?? '';
  return x < y ? -1 : x > y ? 1 : 0;
}

/**
 * Whether a column's `referenced*` target is a REAL FK constraint: an FK not
 * flagged `isLoose`. Table Explorer's `buildForeignKeyMap` and the VQE column
 * adapter (`toTableExplorerColumn`) both let a real FK win over a loose
 * declaration on the same column (`realFkOutranksLoose`) and leave `isLoose`
 * unset for it, so the lookup (preview, picker target, target filter) must
 * then read the constraint, never a declaration it resolves on its own —
 * whether that declaration conflicts with the constraint or repeats its
 * endpoints with a `targetFilter` of its own.
 */
export function hasRealForeignKeyTarget(
  column:
    | {
        readonly isForeignKey: boolean;
        readonly isLoose?: boolean;
        readonly referencedTable?: string;
        readonly referencedColumn?: string;
      }
    | undefined,
): boolean {
  return !!column?.isForeignKey && !column.isLoose && !!column.referencedTable && !!column.referencedColumn;
}

/**
 * The `targetFilter` of the loose FK whose source AND target match `column`
 * (the filter chip's lookup scoping). Matching both endpoints picks the right
 * declaration when one column has several (polymorphic associations). A column
 * whose target is a real FK constraint has none: the constraint wins over every
 * declaration on it. `tableSchema` / the target schema are `''` on schemaless
 * engines — present, not missing (`== null` checks).
 */
export function looseTargetFilterForColumn(
  looseRelationships: readonly LooseRelationship[] | undefined,
  tableSchema: string | undefined,
  tableName: string | undefined,
  fromColumn: string,
  column:
    | {
        readonly isForeignKey: boolean;
        readonly isLoose?: boolean;
        readonly referencedSchema?: string;
        readonly referencedTable?: string;
        readonly referencedColumn?: string;
      }
    | undefined,
): FilterGroup[] | undefined {
  if (tableSchema == null || !tableName || hasRealForeignKeyTarget(column)) return undefined;
  const toSchema = column?.referencedSchema;
  const toTable = column?.referencedTable;
  const toColumn = column?.referencedColumn;
  if (toSchema == null || !toTable || !toColumn) return undefined;
  return (looseRelationships ?? []).find(
    (r) =>
      r.fromSchema === tableSchema &&
      r.fromTable === tableName &&
      r.fromColumn === fromColumn &&
      r.toSchema === toSchema &&
      r.toTable === toTable &&
      r.toColumn === toColumn,
  )?.targetFilter;
}

/**
 * The target filter for the relationship a lookup is ACTUALLY reading: once a
 * loose declaration is active (resolved by `sourcePath`, picked in the
 * multi-FK chooser, or pre-selected), its OWN `targetFilter` — possibly none —
 * never the caller's, which was resolved for the column's first declaration
 * and belongs to another relationship when the column hosts several. The
 * caller's filter applies only when no loose declaration is active (a real FK,
 * or a caller that scoped the target itself).
 */
export function resolveActiveTargetFilter(
  activeLooseRel: LooseRelationship | null,
  callerTargetFilter: FilterGroup[] | undefined,
): FilterGroup[] | undefined {
  return activeLooseRel ? activeLooseRel.targetFilter : callerTargetFilter;
}

/**
 * `schema.table.column` as a display label, leaving out an empty part — a
 * SQLite relationship's schema is '' and must not read as `.orders.id`.
 */
export function qualifiedLabel(...parts: readonly (string | null | undefined)[]): string {
  return parts.filter((p): p is string => !!p).join('.');
}
